import i18nFactory, { i18nRaw as i18nRawFactory } from '@yandex-int/i18n';
import classNames from 'classnames';
import * as React from 'react';

import { Bem } from '../../service/Bem/Bem';
import { UrlHelpers } from '../../service/UrlHelpers';
import { BannersPriceField } from '../../typings/bannerPricesPopupState';
import { BannerPrice } from '../../typings/banners';
import { LinkTarget } from '../../typings/common';
import { BannersPriceEditor2 } from '../BannersPriceEditor2/BannersPriceEditor2';
import { ButtonDecorator } from '../ButtonDecorator/ButtonDecorator';
import { ErrorItem, ErrorsPresenter } from '../ErrorsPresenter/ErrorsPresenter';
import { ErrorsView } from '../ErrorsView/ErrorsView';
import { HintBlock } from '../HintBlock/HintBlock';
import { Shape2, Shape2Icon, Shape2Theme } from '../Shape2/Shape2';
import { StyledLink, StyledLinkTheme } from '../StyledLink/StyledLink';

import './BannerPriceForm.css';
import * as keyset from './BannerPriceForm.i18n';

const i18n = i18nFactory(keyset);
const i18nRaw = i18nRawFactory(keyset);

export interface BannerPriceFormProps {
    hint: boolean;
    isOnProgress?: boolean;
    isHintHidden?: boolean;
    isLoadingHidden?: boolean;
    isPriceRequired?: boolean;
    isAdaptive?: boolean;
    sizeContent?: string;
    cls?: string;
    bannerPrice: BannerPrice;
    errors?: ErrorItem[];
    onChangeDraftBannerPrice: (
        field: BannersPriceField,
        value: string,
        paths?: Array<string>
    ) => void;
    onChangeBannerPrice?: () => void;
    onCancelBannerPrice: () => void;
    onResetDraftBannerPrice?: () => void;
}

const BLOCK = 'banner-price-form';

export class BannerPriceForm extends React.Component<BannerPriceFormProps> {
    render() {
        const {
            bannerPrice,
            onChangeDraftBannerPrice,
            errors = [],
            isPriceRequired,
            sizeContent,
            isLoadingHidden,
            isAdaptive,
            isOnProgress,
            cls
        } = this.props;

        const isProgress = isLoadingHidden ? false : isOnProgress;

        return (
            <ErrorsPresenter errors={errors}>
                <div className={classNames(cls, Bem.cls(BLOCK))}>
                    <div className={Bem.cls(BLOCK, 'content')}>
                        {this.getHint()}
                        <BannersPriceEditor2
                            bannerPrice={bannerPrice}
                            onChange={onChangeDraftBannerPrice}
                            mods={{
                                'size-content': sizeContent || '',
                                adaptive: Boolean(isAdaptive)
                            }}
                            isPriceRequired={isPriceRequired}
                        />
                        {isProgress && this.getProgressBlock()}
                    </div>
                    <ErrorsView cls={Bem.cls(BLOCK, 'error')} path={''} />
                    {this.getButtons()}
                </div>
            </ErrorsPresenter>
        );
    }

    private onClearFields = () => {
        const { onResetDraftBannerPrice, isOnProgress } = this.props;

        if (isOnProgress || !onResetDraftBannerPrice) {
            return;
        }

        onResetDraftBannerPrice();
    };

    private getHint = () => {
        const { hint, isHintHidden } = this.props;

        return hint && !isHintHidden ? (
            <div className={Bem.cls(BLOCK, 'hint-block')}>
                <HintBlock>
                    <>
                        {i18nRaw('price-hint-text', {
                            helpUrl: (
                                <StyledLink
                                    theme={StyledLinkTheme.blue}
                                    href={this.getHelpUrl()}
                                    target={LinkTarget.blank}
                                >
                                    {i18n('about-price')}
                                </StyledLink>
                            )
                        })}
                    </>
                </HintBlock>
            </div>
        ) : null;
    };

    private getProgressBlock = () => {
        return <div className={Bem.cls(BLOCK, 'progress')} />;
    };

    private getButtons = () => {
        const {
            onChangeBannerPrice,
            isOnProgress,
            onCancelBannerPrice,
            onResetDraftBannerPrice
        } = this.props;

        return (
            <div className={Bem.cls(BLOCK, 'buttons')}>
                <div>
                    <ButtonDecorator
                        cls={Bem.cls(BLOCK, 'submit-button')}
                        theme={'action'}
                        size={'n'}
                        width={'max'}
                        text={i18n('editor-save-action')}
                        onClick={onChangeBannerPrice}
                        progress={isOnProgress}
                    />
                    <ButtonDecorator
                        cls={Bem.cls(BLOCK, 'cancel-button')}
                        theme={'pseudo'}
                        size={'n'}
                        width={'max'}
                        text={i18n('editor-cancel-action')}
                        onClick={onCancelBannerPrice}
                        disabled={isOnProgress}
                    />
                </div>
                {onResetDraftBannerPrice && !isOnProgress && (
                    <ButtonDecorator
                        theme={'clear'}
                        size={'n'}
                        cls={Bem.cls(BLOCK, 'clear-button')}
                        onClick={this.onClearFields}
                    >
                        <Shape2
                            width={16}
                            height={16}
                            icon={Shape2Icon.trashbin}
                            theme={Shape2Theme.light}
                        />
                        <span className={Bem.cls(BLOCK, 'clear-button-text')}>
                            {i18n('editor-clear-fields-action')}
                        </span>
                    </ButtonDecorator>
                )}
            </div>
        );
    };

    private getHelpUrl = () => {
        const currentTld = UrlHelpers.getTld();

        return UrlHelpers.getCommonHelpUrl(
            'direct-tooltips/price.html',
            currentTld
        );
    };
}
